import { FieldType } from '@ngx-formly/material';
import { Observable, Subject, BehaviorSubject, of } from 'rxjs';
import { debounceTime, takeUntil, map, catchError } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { formatSnakeCaseToTitleCase } from '@lib/common/helpers';
import { t } from '@lib/translation';

import { ISnSearchListFormFieldConfig } from '../../interfaces/search-list-form-field-config.interface';
import { ISearchListQueryParameters, ISearchListResponse } from '../../interfaces/search-list-template-options.interface';

@Component({
  selector: 'fs-search-list-form-field',
  templateUrl: './search-list-form-field.component.html',
  styleUrls: ['./search-list-form-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchListFormFieldComponent<T = unknown> extends FieldType<ISnSearchListFormFieldConfig<T>> implements OnInit, OnDestroy {
  public defaultOptions: ISnSearchListFormFieldConfig<T>;

  public searchControl = new UntypedFormControl('');
  public selection = new SelectionModel(false, []);
  
  public options$ = new BehaviorSubject<T[]>([]);
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public hasError$ = new BehaviorSubject<boolean>(false);

  private readonly _destroy$ = new Subject<void>();
  private readonly _allOptions$ = new BehaviorSubject<T[]>([]);

  constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.defaultOptions = {
      type: 'search-list',
      templateOptions: {
        appearance: 'fill',
        valueProp: 'id' as keyof T,
        labelProp: 'name' as keyof T,
        debounceTime: 300,
        multiple: false,
        maxHeight: '120px',
        showLoading: true,
        filterLocally: false,
        searchPlaceholder: t('common.searchPlaceholder'),
        noResultsText: t('common.noResultsFound'),
      },
    };
  }

  public ngOnInit(): void {
    if (!this.to) {
      return;
    }
    
    // Set up multiple selection if configured
    this.selection = new SelectionModel(this.to.multiple ?? false, []);
  
    this._initializeSelection();
    this._setupSearch();
    
    if (this.to.filterLocally) {
      this._loadAllData();
    } else {
      // For server-side filtering, start with empty search
      this._loadData('');
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onSelectionChange(option: T): void {
    if (this.to?.multiple) {
      this.selection.toggle(option);
    } else {
      this.selection.clear();
      this.selection.select(option);
    }

    this._updateFormControlValue();
    
    if (this.to?.selectionChange) {
      const selected = this.to.multiple ? this.selection.selected : this.selection.selected?.[0];
      this.to.selectionChange(selected);
    }
  }

  public isSelected(option: T): boolean {
    return this.selection.isSelected(option);
  }

  public getOptionValue(option: T): unknown {
    const valueProp = this.to?.valueProp as string;
    return valueProp ? option[valueProp] : option;
  }

  public getOptionLabel(option: T): string {
    const labelProp = this.to?.labelProp as string;
    return labelProp ? option[labelProp] : option?.toString() || '';
  }

  public getOptionSecondaryLabel(option: T): string {
    const secondaryLabelProp = this.to?.secondaryLabelProp as string;
    return secondaryLabelProp ? option[secondaryLabelProp] : '';
  }

  /**
   * Get the organization type of the option
   * @param option - The option to get the organization type of
   * @returns The organization type of the option. 
   * If the organization type is not a string, return an empty string.
   */
  public getOptionOrganizationType(option: T): string {
    const type = (option as Record<string, unknown>)?.type;
    return type ? formatSnakeCaseToTitleCase(String(type)) : '';
  }

  /**
   * Get the distance of the option
   * @param option - The option to get the distance of
   * @returns The distance of the option. If the distance is not a number, return null.
   * If the distance is a negative number, return 0.
   */
  public getOptionDistance(option: T): number | null {
    const distance = (option as Record<string, unknown>)?.distance;
    return typeof distance === 'number' ? Math.max(0, distance) : null;
  }

  public trackOption = (index: number, option: T): unknown => {
    if (this.to?.trackByFn) {
      return this.to.trackByFn(index, option);
    }
    return this.getOptionValue(option);
  }

  public get searchPlaceholder(): string {
    return this.to?.searchPlaceholder ?? this.defaultOptions.templateOptions.searchPlaceholder;
  }

  public get maxHeight(): string {
    return this.to?.maxHeight ?? this.defaultOptions.templateOptions.maxHeight;
  }

  public get noResultsText(): string {
    return this.to?.noResultsText ?? this.defaultOptions.templateOptions.noResultsText;
  }

  private _setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(this.to?.debounceTime || this.defaultOptions.templateOptions.debounceTime),
        takeUntil(this._destroy$)
      )
      .subscribe((searchTerm: string) => {
        if (this.to?.filterLocally) {
          this._filterLocalData(searchTerm);
        } else {
          this._loadData(searchTerm);
        }
      });
  }

  private _loadData(searchTerm: string): void {
    if (!this.to?.fetchDataFn) {
      return;
    }

    const params: ISearchListQueryParameters = {
      search: searchTerm,
    };

    this.isLoading$.next(true);
    this.hasError$.next(false);

    this.to.fetchDataFn(params, this.model, this.field)
      .pipe(
        map((response: ISearchListResponse<T>) => response.results || []),
        catchError((error) => {
          console.error('Search list error:', error);
          this.hasError$.next(true);
          return of([]);
        }),
        takeUntil(this._destroy$)
      )
      .subscribe({
        next: (options) => {
          this.options$.next(options);
          this.isLoading$.next(false);
          this._changeDetectorRef.markForCheck();
        },
        error: () => {
          this.isLoading$.next(false);
          this.hasError$.next(true);
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  private _loadAllData(): void {
    if (!this.to?.fetchDataFn) {
      return;
    }

    const params: ISearchListQueryParameters = {
      pageSize: this.to.allDataPageSize ?? 'all',
    };

    this.isLoading$.next(true);
    this.hasError$.next(false);

    this.to.fetchDataFn(params, this.model, this.field)
      .pipe(
        map((response: ISearchListResponse<T>) => response.results || []),
        catchError((error) => {
          console.error('Search list error:', error);
          this.hasError$.next(true);
          return of([]);
        }),
        takeUntil(this._destroy$)
      )
      .subscribe({
        next: (options) => {
          this._allOptions$.next(options);
          this.options$.next(options); // Show all options initially
          this.isLoading$.next(false);
          this._changeDetectorRef.markForCheck();
        },
        error: () => {
          this.isLoading$.next(false);
          this.hasError$.next(true);
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  private _filterLocalData(searchTerm: string): void {
    const allOptions = this._allOptions$.getValue();
    
    if (!searchTerm?.trim()) {
      // If no search term, show all options
      this.options$.next(allOptions);
      this._changeDetectorRef.markForCheck();
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filteredOptions = allOptions.filter((option) => {
      const primary = this.getOptionLabel(option);
      if (primary?.toLowerCase().includes(searchLower)) return true;

      const secondary = this.getOptionSecondaryLabel(option);
      return secondary?.toLowerCase().includes(searchLower);
    });

    this.options$.next(filteredOptions);
    this._changeDetectorRef.markForCheck();
  }

  private _initializeSelection(): void {
    if (!this.formControl.value) {
      return;
    }

    const currentValue = this.formControl.value;
    
    if (this.to?.multiple && Array.isArray(currentValue)) {
      currentValue.forEach((value) => this.selection.select(value));
    } else if (!this.to?.multiple) {
      this.selection.select(currentValue);
    }
  }

  private _updateFormControlValue(): void {
    const selected = this.selection.selected;
    
    if (this.to?.multiple) {
      const values = selected.map((option) => this.getOptionValue(option));
      this.formControl.setValue(values);
    } else {
      const value = selected.length > 0 ? this.getOptionValue(selected[0]) : null;
      this.formControl.setValue(value);
    }
  }
}
